import { useEffect, useMemo, useState } from "react";

import ReferenceRecord from "../../../../components/Records/References/ReferenceRecord";
import useFilePreview from "../../../../api/api-hooks/records/useFilePreview";
import TextRecord from "../../../../components/Records/Text/TextRecord";
import useRecord from "../../../../api/api-hooks/records/useRecord";
import { FileInterface } from "../../../../types/RecordInterfaces";
import AppDrawer from "../../../../components/Drawers/AppDrawer";
import { isoStringToFormat } from "../../../../utils/DateUtils";
import Constants from "../../../../utils/Constants";

interface Props {
  parentRecordID: string;
  record: any;
  open: boolean;
  file: FileInterface;
  submitting: boolean;
  onClose: () => void;
  onNameUpdate: (payload: any, onSuccess?: () => void) => void;
}

const ImageInfoDrawer = ({
  open,
  parentRecordID,
  record,
  file,
  submitting,
  onClose,
  onNameUpdate,
}: Props) => {
  const { fileSource, loading } = useFilePreview(file);
  const [fileName, setFileName] = useState(file?.Name);
  const [resolution, setResolution] = useState<{ x: number; y: number }>();
  const imageSize = useMemo(() => {
    if (!file?.Size) return "--";
    const sizeStr = ["Bytes", "KB", "MB", "GB"];
    let i = 0;
    while (file?.Size > 900) {
      file.Size /= 1024;
      i++;
    }
    return Math.round(file.Size * 100) / 100 + " " + sizeStr[i];
  }, [file]);

  useEffect(() => {
    if (file?.Name) setFileName(file?.Name);

    if (loading) setResolution(undefined);

    if (!file?.Type?.startsWith("image")) return setResolution(undefined);

    const img = document.createElement("img");
    img.id = `img-gallery-infoDrawer`;
    img.src = fileSource;
    img.className += "hidden";
    document.body.appendChild(img);
    img.onload = () => {
      setResolution({ x: img.naturalWidth, y: img.naturalHeight });
    };
    img.remove();
  }, [file, fileSource, loading]);

  const { record: contact } = useRecord({
    moduleAPIName: "Contacts",
    recordID:
      typeof file?.UploadedBy === "string"
        ? file?.UploadedBy
        : file?.UploadedBy?.id,
    enabled: open && typeof file?.UploadedBy === "string",
  });

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      placement="right"
      headerTitle="Info"
      body={
        <div>
          <div className="w-50">
            <h4 className="m-0">File Name:</h4>
            <div className="my--2">
              <TextRecord
                field={{ APIName: "Name", FieldType: "Text" }}
                mode="rw"
                recordID={parentRecordID}
                inputSize="sm"
                onUpdate={(res: any) => {
                  onNameUpdate(res.payload, () => {
                    setFileName(res.payload.Name);
                    res?.onSuccess?.();
                  });
                }}
                submitting={submitting}
                type="Text"
                existingValue={fileName}
                isDrawer={true}
                tableView={false}
                modifyTrigger={"click"}
                error={null}
              />
            </div>
          </div>
          <div className="my-3">
            <h4 className="m-0">Uploaded Date:</h4>
            <span>
              {isoStringToFormat(
                file.UploadedDate,
                Constants.FIELDTYPES.DATE_TIME
              )}
            </span>
          </div>
          <div className="my-3">
            <h4 className="m-0">Size:</h4>
            <span>{imageSize}</span>
          </div>
          {resolution && (
            <div className="my-3">
              <h4 className="m-0">Resolution:</h4>
              <span>{`${resolution?.x}px - ${resolution?.y}px`}</span>
            </div>
          )}
          <div className="my-3">
            <h4 className="m-0">Created by:</h4>
            {/* @ts-ignore */}
            <ReferenceRecord
              field={{
                APIName: "CreatedBy",
                FieldType: "SingleReference",
                ModuleAPIName: "Contacts",
                Name: "Created By",
                ReadOnly: true,
                SourcesList: "Tenant.Modules.Contacts",
                SystemField: true,
              }}
              record={record}
              existingValue={
                contact
                  ? {
                      id: contact.id,
                      _ModuleAPIName: "Contacts",
                      _RecordName: contact?._RecordName,
                    }
                  : typeof file.UploadedBy === "string"
                  ? {
                      id: file.UploadedBy,
                    }
                  : file.UploadedBy ?? null
              }
            />
          </div>
        </div>
      }
    />
  );
};

export default ImageInfoDrawer;
